import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { useCallback } from "react";
import {
  ORGANISATION_BRANDS_QUERY_KEY,
  ORGANISATION_LIST_QUERY_KEY,
} from "../constants";

import { AI_CONFIGURATION_QUERY_KEY } from "@entities/AIControls/lib/constants";
import { IAIConfiguration } from "@entities/AIControls/lib/types";
import { BackednResponse, IBrand } from "@shared/lib/types";
import { OrganisationService } from "../services/organisation.service";

const QUERY_OPTIONS = {
  queryKey: ORGANISATION_LIST_QUERY_KEY,
  skipInvalidation: false,
};

export function useEditBrand(queryKey?: string[], handleSuccess?: () => void) {
  const assignUserBrand = useCallback(
    ({ brandId, brand }: { brandId: string; brand: Partial<IBrand> }) =>
      OrganisationService.updateBrand(brand, brandId).catch(async (err) => {
        const responseBody = await err.response.json();
        ToastService.error(responseBody.msg);
        throw err;
      }),
    []
  );

  const {
    mutate: update,
    isLoading: isUpdating,
    isSuccess: isUpdated,
    error: updateError,
    variables,
  } = useMutation({
    mutationFn: assignUserBrand,
    options: QUERY_OPTIONS,
    onSuccess: (c, data, { brandId }) => {
      c.invalidateQueries({
        queryKey: [...ORGANISATION_LIST_QUERY_KEY, brandId],
      });
      c.invalidateQueries({
        queryKey: [
          ...ORGANISATION_BRANDS_QUERY_KEY,
          data?.data?.brandOrganisationId,
        ],
      });
      c.setQueryData(
        [...AI_CONFIGURATION_QUERY_KEY, brandId],
        (oldData: BackednResponse<IAIConfiguration>) => ({
          ...oldData,
          data: {
            ...oldData?.data,
            isAiChecksEnabled: data?.data?.isAiChecksEnabled,
            isAiConsistencyCheck: data?.data?.isAiConsistencyCheck,
            isAiScriptVsDraftingConsistencyCheck:
              data?.data?.isAiScriptVsDraftingConsistencyCheck,
            isAiSafetyChecks: data?.data?.isAiSafetyChecks,
            isAiPerformanceChecks: data?.data?.isAiPerformanceChecks,
            isAiBrandGuardRailsChecks: data?.data?.isAiBrandGuardRailsChecks,
            isAiAdvancedPromptingChecks:
              data?.data?.isAiAdvancedPromptingChecks,
            aiBrandGuardRailsChecks: data?.data?.aiBrandGuardRailsChecks,
            aiAdvancedPromptingChecks: data?.data?.aiAdvancedPromptingChecks,
          },
        })
      );
      if (queryKey) {
        c.invalidateQueries({ queryKey: queryKey });
      }
      if (handleSuccess) {
        handleSuccess();
      }
    },
  });

  return {
    update,
    isUpdating,
    isUpdated,
    variables,
    updateError,
  };
}
